import React, { useEffect, useRef, useState } from 'react';
import { PipecatClient } from '@pipecat-ai/client-js';
import { SmallWebRTCTransport } from '@pipecat-ai/small-webrtc-transport';
import './TalkToTakku.css';

interface TalkToTakkuProps {
  isOpen: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  userToken: string | null;
  // Owned by App.tsx, not this component -- must be an element that's
  // already mounted (and play()/pause()'d) at the moment the mic button is
  // clicked, for iOS Safari to treat it as unlocked for autoplay. This
  // component's own JSX only mounts once isOpen flips true, which is too
  // late; a DIFFERENT <audio> element getting unlocked doesn't count, iOS
  // Safari tracks it per-element. See App.tsx's unlockAudio().
  //
  // We attach Takku's remote stream to this element (muted) purely to keep
  // Chrome's WebRTC decoder running -- see the note in onTrackStarted.
  audioRef: React.RefObject<HTMLAudioElement | null>;
  // Actual playback path. Confirmed live (2026-08-27): iOS Safari plays
  // back genuinely nothing for Takku's track via <audio>.srcObject even
  // fully unlocked and play() resolving with no error -- matches multiple
  // long-standing unresolved WebKit reports (bugs.webkit.org #230902, Apple
  // dev forum 713573) of exactly this. Web Audio API is a different code
  // path that doesn't hit it. Must be created/resumed synchronously in the
  // same click gesture as the <audio> unlock -- see App.tsx's unlockAudio().
  audioContextRef: React.RefObject<AudioContext | null>;
}

type CallState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error';

// Full Gemini Live quality "Talk to Takku" voice mode -- the same
// GeminiLiveLLMService pipeline used for phone calls, reached here via a
// real WebRTC peer connection instead of a SignalWire call.
//
// Originally built on @pipecat-ai/websocket-transport (raw WebSocket +
// PCM frames), which played Takku's audio back through a plain <audio>
// element with no real WebRTC track -- the browser's echo canceller had
// no reference signal for it, so users heard themselves in real time on
// every platform. Rebuilt on @pipecat-ai/small-webrtc-transport
// (aiortc-backed on the server -- see backend/voice/pipecat_handler.py) so
// capture and playback share one RTCPeerConnection and native AEC actually
// has something to cancel against. See fix_browser_voice_webrtc_transport.py.
const TalkToTakku: React.FC<TalkToTakkuProps> = ({ isOpen, onClose, apiBaseUrl, userToken, audioRef, audioContextRef }) => {
  const [callState, setCallState] = useState<CallState>('connecting');
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<PipecatClient | null>(null);
  // Tracks the Web Audio node currently routing Takku's voice to the
  // speaker, so it can be torn down cleanly on disconnect/reconnect instead
  // of leaking a connection into audioContext.destination each call.
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  // Which track.id sourceNodeRef is currently wired to -- see the dedupe
  // check in onTrackStarted.
  const currentTrackIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    // Capture the always-mounted <audio> element (a prop ref from App.tsx, set
    // for the app's whole lifetime) so the cleanup below references a stable
    // value rather than reading audioRef.current at teardown time.
    const audioEl = audioRef.current;
    setCallState('connecting');
    setError(null);

    const transport = new SmallWebRTCTransport({
        // STUN for NAT traversal. A TURN relay (self-hosted coturn) can be
        // added via REACT_APP_TURN_* build-time env vars -- normal ICE then
        // uses it only as a fallback when a direct path can't be found.
        // Same credentials as the backend's TURN_SERVER_IP/USERNAME/PASSWORD,
        // just REACT_APP_-prefixed (CRA bakes them in at build time -- no
        // runtime env vars in a static build). No hardcoded credential
        // fallback: it's a real secret, doesn't belong in source.
        iceServers: process.env.REACT_APP_TURN_URL
          ? [
              { urls: 'stun:stun.l.google.com:19302' },
              {
                urls: process.env.REACT_APP_TURN_URL,
                username: process.env.REACT_APP_TURN_USERNAME,
                credential: process.env.REACT_APP_TURN_PASSWORD,
              },
            ]
          : [{ urls: 'stun:stun.l.google.com:19302' }],
    });

    const client = new PipecatClient({
      transport,
      enableMic: true,
      enableCam: false,
      callbacks: {
        onConnected: () => {
          if (!cancelled) setCallState('listening');
        },
        onDisconnected: () => {
          if (!cancelled) setCallState('connecting');
        },
        onTrackStarted: (track: MediaStreamTrack) => {
          // SmallWebRTCTransport always creates audio+video+screenVideo
          // transceivers and fires onTrackStarted for ALL of them, even
          // though video/screen are disabled. Only the audio track matters.
          if (track.kind !== 'audio') return;
          if (cancelled || !audioContextRef.current) return;
          // Desktop consistently fires onTrackStarted TWICE for kind=audio
          // (mobile only once). If it's the SAME track re-firing (a
          // legitimate "unmute" can do that), don't tear down the working
          // graph. A genuinely different track.id still gets rewired below.
          if (sourceNodeRef.current && track.id === currentTrackIdRef.current) return;

          // Chromium bug #121673: a remote WebRTC audio track routed ONLY
          // through Web Audio (createMediaStreamSource) never gets its
          // decoder pumped -- it just outputs silence, even with packets
          // arriving cleanly. The track must ALSO be sunk into an
          // HTMLMediaElement. So attach the stream to the (App.tsx-owned,
          // already-unlocked) <audio> element, muted, purely to keep the
          // decoder alive. Actual sound comes from the Web Audio graph
          // below -- which is also what iOS needs, since iOS Safari plays
          // nothing for this track via <audio>.srcObject.
          try {
            if (sourceNodeRef.current) sourceNodeRef.current.disconnect();
            const stream = new MediaStream([track]);
            if (audioRef.current) {
              audioRef.current.srcObject = stream;
              audioRef.current.muted = true;
              audioRef.current.play().catch(() => {});
            }
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(audioContextRef.current.destination);
            sourceNodeRef.current = source;
            currentTrackIdRef.current = track.id;
            audioContextRef.current.resume().catch(() => {});
          } catch (e: any) {
            console.error('Talk to Takku: failed to wire audio', e);
          }
        },
        onBotStartedSpeaking: () => {
          if (!cancelled) setCallState('speaking');
        },
        onBotStoppedSpeaking: () => {
          if (!cancelled) setCallState('listening');
        },
      },
    });

    clientRef.current = client;

    client
      .connect({
        webrtcRequestParams: {
          endpoint: `${apiBaseUrl}/voice/webrtc-offer`,
          // A real Authorization header -- unlike the old WebSocket path,
          // a POST can carry one, so no more JWT-in-query-string.
          headers: new Headers(userToken ? { Authorization: `Bearer ${userToken}` } : {}),
        },
      })
      .catch((err: any) => {
        if (cancelled) return;
        console.error('Talk to Takku connection error:', err);
        setError("Couldn't connect. Check your connection and try again.");
        setCallState('error');
      });

    return () => {
      cancelled = true;
      client.disconnect().catch(() => {});
      clientRef.current = null;
      if (audioEl) {
        audioEl.pause();
        audioEl.srcObject = null;
      }
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }
      currentTrackIdRef.current = null;
    };
  }, [isOpen, apiBaseUrl, userToken, audioRef, audioContextRef]);

  const handleClose = () => {
    clientRef.current?.disconnect().catch(() => {});
    onClose();
  };

  if (!isOpen) return null;

  let stateLabel = 'Connecting...';
  if (callState === 'listening') stateLabel = 'Listening...';
  else if (callState === 'thinking') stateLabel = 'Thinking...';
  else if (callState === 'speaking') stateLabel = 'Speaking...';
  else if (callState === 'error') stateLabel = 'Connection error';

  return (
    <div className="talk-to-takku-overlay">
      {/* <audio> element lives in App.tsx (talkToTakkuAudioRef), not here --
          must already be mounted+unlocked before this overlay ever opens.
          See the audioRef prop's comment above. */}

      <button className="talk-to-takku-close" onClick={handleClose} aria-label="End voice chat">
        ✕
      </button>

      <div className={`talk-to-takku-orb ${callState}`}>
        <span className="talk-to-takku-orb-icon">🐱</span>
      </div>

      <div className="talk-to-takku-state">{stateLabel}</div>

      {error && <div className="talk-to-takku-error">{error}</div>}

      <button className="talk-to-takku-end-btn" onClick={handleClose}>
        End voice chat
      </button>
    </div>
  );
};

export default TalkToTakku;
